import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import { MdOutlinePerson, MdRefresh } from "react-icons/md";
import { AiFillGolden } from "react-icons/ai";
import { RiDeviceRecoverLine } from "react-icons/ri";
import { VscEyeClosed, VscEye } from "react-icons/vsc";
import API_DOMAIN from "../config/config";
import dayjs from "dayjs";
import "./tablecus.css";
import "bootstrap/dist/css/bootstrap.min.css";
import LoadingOverlay from "../components/LoadingOverlay";
import { GrPrevious, GrNext } from "react-icons/gr";
import { pdf } from "@react-pdf/renderer";
import DashBoardPDF from "../pdf/DashBoardPDF";
import MonthElevenNoticePDF from "../pdf/notice12month";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import { ClickButton } from "../components/ClickButton";
import { TextInputForm } from "../components/Forms";

const isAlertEligible = (pawnDateStr, agreedPeriodStr) => {
  if (!pawnDateStr) return false;

  const elapsedDays = dayjs().diff(dayjs(pawnDateStr), 'day');

  let limitDays = 360; // Default 1 year (Excel 360 days convention)
  if (agreedPeriodStr) {
    const str = agreedPeriodStr.toString().toLowerCase();
    const num = parseInt(str) || 1;
    if (str.includes("year")) limitDays = num * 360;
    else if (str.includes("month")) limitDays = num * 30;
    else if (str.includes("day")) limitDays = num;
  }

  return elapsedDays > limitDays;
};

const HighValueGoldLoans = () => {
  // Authorization State - Always prompt on enter
  const [authorized, setAuthorized] = useState(false);

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  const [usernameInput, setUsernameInput] = useState(currentUser?.login_User_Name || "");
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [authError, setAuthError] = useState("");

  // Report Interface State
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [customerData, setcustomerData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [allPawnData, setAllPawnData] = useState([]);
  const [interestData, setInterestData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDays, setFromDays] = useState("");
  const [toDays, setToDays] = useState("");
  const [alertStatusFilter, setAlertStatusFilter] = useState("");

  const [sentWhatsAppAlerts, setSentWhatsAppAlerts] = useState(() => {
    try {
      if (window.location.hostname !== "srivigneshwarafinance.zentexus.com" && window.location.hostname !== "localhost") return [];
      const stored = localStorage.getItem("sentWhatsAppAlerts");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const [sentSmsAlerts, setSentSmsAlerts] = useState(() => {
    try {
      if (window.location.hostname !== "srivigneshwarafinance.zentexus.com" && window.location.hostname !== "localhost") return [];
      const stored = localStorage.getItem("sentSmsAlerts");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const clearSentAlerts = () => {
    setSentWhatsAppAlerts([]);
    setSentSmsAlerts([]);
    localStorage.removeItem("sentWhatsAppAlerts");
    localStorage.removeItem("sentSmsAlerts");
  };

  const [totalRecords, setTotalRecords] = useState(0);              // Customers
  const [totalPawnRecords, setTotalPawnRecords] = useState(0);      // Pawn jewelry (amount >= 20000)
  const [filteredPawnRecords, setFilteredPawnRecords] = useState(0); // Filtered count
  const [pawnrecovertotalRecords, setpawnrecoverTotalRecords] = useState(0); // Recovered (amount >= 20000)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Track if current fetch is from major change (tab/page)
  const isMajorChangeRef = useRef(false);
  const debounceTimer = useRef(null);

  const parsePeriod = (period) => {
    let years = 0;
    let months = 0;
    let days = 0;

    if (!period) return { months, days };

    const yearMatch = period.match(/(\d+)\s*year(s)?/i);
    const monthMatch = period.match(/(\d+)\s*month(s)?/i);
    const dayMatch = period.match(/(\d+)\s*day(s)?/i);

    if (yearMatch) years = parseInt(yearMatch[1], 10);
    if (monthMatch) months = parseInt(monthMatch[1], 10);
    if (dayMatch) days = parseInt(dayMatch[1], 10);

    months += years * 12;

    return { months, days };
  };

  const formatPeriod = (totalMonths) => {
    const finalMonths = totalMonths;
    return `${finalMonths} month${finalMonths !== 1 ? "s" : ""}`;
  };

  const aggregateInterestData = useMemo(() => {
    const aggregated = interestData.reduce((acc, item) => {
      const receiptNo = item.receipt_no;
      if (!acc[receiptNo]) {
        acc[receiptNo] = {
          total_interest_income: 0,
          total_months: 0,
        };
      }
      acc[receiptNo].total_interest_income += parseFloat(
        item.interest_income || 0
      );

      const { months } = parsePeriod(item.interest_payment_periods);
      acc[receiptNo].total_months += months;
      return acc;
    }, {});

    Object.keys(aggregated).forEach((receiptNo) => {
      const { total_months } = aggregated[receiptNo];
      aggregated[receiptNo].interest_payment_periods = formatPeriod(total_months);
    });

    return aggregated;
  }, [interestData]);

  // Handle password verification
  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setAuthError("");

    const username = usernameInput || currentUser?.login_User_Name;
    if (!username) {
      setAuthError("பயனர் பெயர் தேவை (Username is required)");
      return;
    }
    if (!passwordInput) {
      setAuthError("கடவுச்சொல் தேவை (Password is required)");
      return;
    }

    setVerifying(true);
    try {
      const loginData = {
        user_name: username,
        password: passwordInput,
      };

      const response = await fetch(`${API_DOMAIN}/login.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });
      console.log(JSON.stringify(loginData));

      const responseData = await response.json();
      console.log(responseData);

      if (responseData.head.code === 200) {
        setAuthorized(true);
        toast.success("வெற்றிகரமாக திறக்கப்பட்டது!", {
          position: "top-center",
          autoClose: 1500,
          theme: "colored",
        });
      } else {
        setAuthError(responseData.head.msg || "தவறான கடவுச்சொல் (Incorrect Password)");
      }
    } catch (error) {
      console.error("Unlock verification error:", error);
      setAuthError("வலையமைப்பு பிழை! (Network connection error)");
    } finally {
      setVerifying(false);
    }
  };

  // Fetch all pawn data once
  const loadAllPawnData = useCallback(async () => {
    try {
      const response = await fetch(`${API_DOMAIN}/pawnjewelry.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_text: "", action: "without_pagination" }),
      });
      const responseData = await response.json();
      if (responseData.head.code === 200) {
        const processedData = responseData.body.pawnjewelry.map((item) => ({
          ...item,
          jewel_product: JSON.parse(item.jewel_product || "[]"),
          proof: typeof item.proof === 'string' ? JSON.parse(item.proof) : item.proof,
          proof_base64code: typeof item.proof_base64code === 'string' ? JSON.parse(item.proof_base64code) : item.proof_base64code,
        }));
        setAllPawnData(processedData);
        // Calculate totals for gold loans >= 20,000
        const highValuePawns = processedData.filter((item) => item.original_amount >= 20000);
        setTotalPawnRecords(highValuePawns.length);
        const recoveredHighValuePawns = highValuePawns.filter((item) => item.status === "Closed");
        setpawnrecoverTotalRecords(recoveredHighValuePawns.length);
      }
    } catch (error) {
      console.error("Error fetching all pawn data:", error);
    }
  }, []);

  // Client-side filtering & pagination for High Value Report
  const processPawnData = useCallback(() => {
    if (allPawnData.length === 0) return;

    if (isMajorChangeRef.current) {
      setTableLoading(true);
    }

    let filtered = allPawnData;

    // 1. High Value Filter (>= 20,000)
    filtered = filtered.filter((item) => item.original_amount >= 20000);

    // 2. Search & Place Filters
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const mobile = item.mobile_number ? item.mobile_number.toString() : "";
        const receipt = item.receipt_no ? item.receipt_no.toString() : "";
        const custName = item.name ? item.name.toLowerCase() : "";
        const searchLower = searchTerm.toLowerCase();
        return mobile.includes(searchLower) || receipt.includes(searchLower) || custName.includes(searchLower);
      });
    }

    if (placeSearchTerm) {
      filtered = filtered.filter((item) =>
        String(item.place || "").toLowerCase().includes(placeSearchTerm.toLowerCase())
      );
    }

    // 3. Date Filters
    if (fromDate || toDate) {
      filtered = filtered.filter((item) => {
        let matchesDate = true;
        const itemDate = new Date(item.pawnjewelry_date);
        itemDate.setHours(0, 0, 0, 0);

        if (fromDate) {
          const start = new Date(fromDate);
          start.setHours(0, 0, 0, 0);
          if (itemDate < start) matchesDate = false;
        }
        if (toDate) {
          const end = new Date(toDate);
          end.setHours(0, 0, 0, 0);
          if (itemDate > end) matchesDate = false;
        }
        return matchesDate;
      });
    }

    // 4. Status Filter
    if (statusFilter === "recovered") {
      filtered = filtered.filter((item) => item.status === "Closed");
    } else if (statusFilter === "active") {
      filtered = filtered.filter((item) => item.status !== "Closed");
    }

    // 5. Pending Days Filter
    if (fromDays !== "" || toDays !== "") {
      filtered = filtered.filter((item) => {
        const { months, days } = parsePeriod(item.interest_payment_period);
        const totalPendingDays = Math.round((months * 30.4167) + days);

        let matchesDays = true;
        if (fromDays !== "" && totalPendingDays < parseInt(fromDays, 10)) {
          matchesDays = false;
        }
        if (toDays !== "" && totalPendingDays > parseInt(toDays, 10)) {
          matchesDays = false;
        }
        return matchesDays;
      });
    }

    // 6. Alert Status Filter
    if (alertStatusFilter === "sent") {
      filtered = filtered.filter((item) => sentWhatsAppAlerts.includes(item.receipt_no) || sentSmsAlerts.includes(item.receipt_no));
    } else if (alertStatusFilter === "unsent") {
      filtered = filtered.filter((item) => !sentWhatsAppAlerts.includes(item.receipt_no) && !sentSmsAlerts.includes(item.receipt_no));
    }

    setFilteredPawnRecords(filtered.length);
    setFilteredData(filtered);

    // Dynamically update count cards based on filtered results
    setTotalPawnRecords(filtered.length);
    setpawnrecoverTotalRecords(filtered.filter((item) => item.status === "Closed").length);

    // Pagination
    const offset = (currentPage - 1) * itemsPerPage;
    const paginated = filtered.slice(offset, offset + itemsPerPage);

    setUserData(paginated);

    if (isMajorChangeRef.current) {
      setTableLoading(false);
      isMajorChangeRef.current = false;
    }
  }, [allPawnData, currentPage, itemsPerPage, searchTerm, placeSearchTerm, fromDate, toDate, statusFilter, fromDays, toDays, alertStatusFilter, sentWhatsAppAlerts, sentSmsAlerts]);

  const fetchDatajewelpawncustomer = async () => {
    try {
      const response = await fetch(`${API_DOMAIN}/customer.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_text: "", action: "without_pagination" }),
      });
      const responseData = await response.json();
      if (responseData.head.code === 200) {
        setcustomerData(responseData.body.customer);
        setTotalRecords(responseData.body.total_records);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  const fetchinterestData = async () => {
    try {
      const response = await fetch(`${API_DOMAIN}/interest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_text: "" }),
      });
      const responseData = await response.json();
      if (responseData.head.code === 200) {
        setInterestData(responseData.body.interest);
      }
    } catch (error) {
      console.error("Error fetching interest data:", error);
    }
  };

  const triggerSearch = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      triggerSearch();
    }, 600);
  };

  const handleClear = () => {
    setSearchTerm("");
    setPlaceSearchTerm("");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setFromDays("");
    setToDays("");
    setAlertStatusFilter("");
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const dataToExport = filteredData.map(item => ({
      "தேதி": dayjs(item.pawnjewelry_date).format("DD-MM-YYYY"),
      "அடகு எண்": item.receipt_no,
      "வாடிக்கையாளர் பெயர்": item.name,
      "இடம்": item.place,
      "மொபைல் எண்": item.mobile_number,
      "அசல் தொகை": item.original_amount,
      "வட்டி விகிதம்": item.interest_rate,
      "மொத்த எடை": item.total_weight,
      "வட்டி வரவு": aggregateInterestData[item.receipt_no]?.total_interest_income || 0,
      "வட்டி நிலுவை": item.interest_payment_amount || 0,
      "நிலுவை வட்டி நாட்கள்": aggregateInterestData[item.receipt_no]?.interest_payment_periods || "0 days",
      "மொத்த தொகை": (parseFloat(item.original_amount || 0) + parseFloat(item.interest_payment_amount || 0) + parseFloat(aggregateInterestData[item.receipt_no]?.total_interest_income || 0)).toFixed(2),
      "1 கிராம்": ((parseFloat(item.original_amount || 0) + parseFloat(item.interest_payment_amount || 0) + parseFloat(aggregateInterestData[item.receipt_no]?.total_interest_income || 0)) / parseFloat(item.total_weight || 1)).toFixed(2),
      "ஒப்பு காலம்": item.Jewelry_recovery_agreed_period || "-",
      "Status": item.status === "Closed" ? "நகை மீட்கபட்டது" : "நகை மீட்கபடவில்லை"
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pawn Details");
    XLSX.writeFile(workbook, "High_Value_Pawn_Details.xlsx");
  };

  const exportToPDF = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }
    const loadToast = toast.loading("Generating PDF...");
    try {
      const blob = await pdf(
        <DashBoardPDF
          data={filteredData}
          aggregateInterestData={aggregateInterestData}
        />
      ).toBlob();
      saveAs(blob, "High_Value_Pawn_Details.pdf");
      toast.dismiss(loadToast);
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error(error);
      toast.update(loadToast, { render: "Failed to generate PDF", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const downloadNotice = async (item) => {
    const loadToast = toast.loading("Generating Notice PDF...");
    try {
      const blob = await pdf(
        <MonthElevenNoticePDF
          date={dayjs().format("DD/MM/YYYY")}
          customerName={item.name}
          address={item.place || ""}
          loanNumber={item.receipt_no}
          pawnDate={item.pawnjewelry_date}
          loanAmount={item.original_amount}
          interest={item.interest_payment_amount || 0}
          fatherName={item.father_name || ""}
          mobileNumber={item.mobile_number}
          totalWeight={item.total_weight}
          jewelDetails={item.pledged_items}
          agreedPeriod={item.Jewelry_recovery_agreed_period}
        />
      ).toBlob();
      saveAs(blob, `Notice_${item.receipt_no}.pdf`);
      toast.dismiss(loadToast);
      toast.success("Notice generated successfully");
    } catch (error) {
      console.error(error);
      toast.update(loadToast, { render: "Failed to generate Notice", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  // Initial load once authorized
  useEffect(() => {
    if (!authorized) return;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDatajewelpawncustomer(),
          fetchinterestData(),
          loadAllPawnData(),
        ]);
      } catch (err) {
        console.error("Initial load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [authorized, loadAllPawnData]);

  // Re-process data when pagination or search changes
  useEffect(() => {
    if (authorized) {
      processPawnData();
    }
  }, [authorized, processPawnData]);

  // When page changes → mark as major change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    isMajorChangeRef.current = true;
  };

  const getTamilAlertMessage = (customerName, receiptNo, pawnDate) => {
    return `திரு / திருமதி ${customerName}, அடகு எண் - ${receiptNo}, நகை தேதி ${dayjs(
      pawnDate
    ).format("DD-MM-YYYY")} அன்று வைக்கப்பட்டுள்ளது. வட்டி செலுத்த வேண்டிய தேதி கடந்து விட்டதால், தங்களது நகையை அசல் தொகையை கட்டித் திருப்பி கொள்ளுமாறும் அல்லது வட்டித் தொகையை கட்டி தங்களது நகையை புதுப்பித்துக் கொள்ளுமாறும் கேட்டுக் கொள்கிறோம்.\n\nஸ்ரீ விக்னேஷ்வரா பைனான்ஸ்`;
  };

  const generateWhatsAppURL = (mobileNumber, message) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/91${mobileNumber}?text=${encodedMessage}`;
  };

  const renderTable = (data) => (
    <div className="balance-table-wrapper">
      <Table bordered hover responsive className="table">
        <thead>
          <tr>
            <th>Customer Photo</th>
            <th>Jewel Photo</th>
            <th>தேதி</th>
            <th>அடகு எண்</th>
            <th>வாடிக்கையாளர் பெயர்</th>
            <th>இடம்</th>
            <th>மொபைல் எண்</th>
            <th>அசல் தொகை</th>
            <th>வட்டி விகிதம்</th>
            <th>நகை எடை</th>
            <th>S/WGT எடை</th>
            <th>மொத்த எடை</th>
            <th>அடகு பொருட்கள்</th>
            <th>நகை மதிப்பு (அடகு)</th>
            <th>வட்டி நிலுவை</th>
            <th>வட்டி செலுத்தியது</th>
            <th>மொத்த மதிப்பீடு</th>
            <th>தற்போதைய நகை மதிப்பு</th>
            <th>நகை மீட்பு ஒப்புக்கொண்ட காலம்</th>
            <th>alert</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item) => (
              <tr key={item.id}>
                <td>
                  {(() => {
                    const proofs = item.proof || [];
                    const src = proofs[0];
                    if (src) {
                      return (
                        <img
                          src={src}
                          alt="Customer"
                          style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer", borderRadius: "5px" }}
                          onClick={() => {
                            setSelectedImage(src);
                            setShowImageModal(true);
                          }}
                        />
                      );
                    }
                    const customer = customerData.find(c => c.customer_no === item.customer_no);
                    const custSrc = customer && customer.proof ? customer.proof[0] : null;
                    if (custSrc) {
                      return (
                        <img
                          src={custSrc}
                          alt="Customer"
                          style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer", borderRadius: "5px" }}
                          onClick={() => {
                            setSelectedImage(custSrc);
                            setShowImageModal(true);
                          }}
                        />
                      );
                    }
                    return "-";
                  })()}
                </td>
                <td>
                  {(() => {
                    const proofs = item.proof || [];
                    const src = proofs[1];
                    if (src) {
                      return (
                        <img
                          src={src}
                          alt="Jewel"
                          style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer", borderRadius: "5px" }}
                          onClick={() => {
                            setSelectedImage(src);
                            setShowImageModal(true);
                          }}
                        />
                      );
                    }
                    const customer = customerData.find(c => c.customer_no === item.customer_no);
                    const custSrc = customer && customer.proof ? customer.proof[1] : null;
                    if (custSrc) {
                      return (
                        <img
                          src={custSrc}
                          alt="Jewel"
                          style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer", borderRadius: "5px" }}
                          onClick={() => {
                            setSelectedImage(custSrc);
                            setShowImageModal(true);
                          }}
                        />
                      );
                    }
                    return "-";
                  })()}
                </td>
                <td>{dayjs(item.pawnjewelry_date).format("DD-MM-YYYY")}</td>
                <td>{item.receipt_no}</td>
                <td>{item.name}</td>
                <td>{item.place}</td>
                <td>{item.mobile_number}</td>
                <td>{item.original_amount}</td>
                <td>{item.interest_rate}</td>
                <td>{item.jewel_weight}</td>
                <td>{item.net_weight}</td>
                <td>{item.total_weight}</td>
                <td>{item.pledged_items}</td>
                <td>{(item.original_amount / item.total_weight).toFixed(2)}</td>
                <td>
                  <div className="dashboard">
                    <span>{item.interest_payment_period}</span>
                    <span style={{ color: "green" }}>
                      ₹{item.interest_payment_amount.toFixed(2)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="dashboard">
                    <span>
                      {aggregateInterestData[item.receipt_no]?.interest_payment_periods || "N/A"}
                    </span>
                    <span style={{ color: "green" }}>
                      ₹{aggregateInterestData[item.receipt_no]?.total_interest_income?.toFixed(2) || 0}
                    </span>
                  </div>
                </td>
                <td>
                  ₹{(
                    parseFloat(item.original_amount || 0) +
                    parseFloat(item.interest_payment_amount || 0) +
                    parseFloat(aggregateInterestData[item.receipt_no]?.total_interest_income || 0)
                  ).toFixed(2)}
                </td>
                <td>
                  ₹{(
                    (parseFloat(item.original_amount || 0) +
                      parseFloat(item.interest_payment_amount || 0) +
                      parseFloat(aggregateInterestData[item.receipt_no]?.total_interest_income || 0)) /
                    item.total_weight
                  ).toFixed(2)}
                </td>
                <td>{item.Jewelry_recovery_agreed_period || "-"}</td>
                <td style={{ textAlign: "center" }}>
                  {item.status !== "Closed" && isAlertEligible(item.pawnjewelry_date, item.Jewelry_recovery_agreed_period) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px", alignItems: "center" }}>
                      <button
                        onClick={() => {
                          const msg = getTamilAlertMessage(item.name, item.receipt_no, item.pawnjewelry_date);
                          const url = generateWhatsAppURL(item.mobile_number, msg);
                          window.open(url, "_blank");
                          if (!sentWhatsAppAlerts.includes(item.receipt_no)) {
                            const newSentAlerts = [...sentWhatsAppAlerts, item.receipt_no];
                            setSentWhatsAppAlerts(newSentAlerts);
                            localStorage.setItem("sentWhatsAppAlerts", JSON.stringify(newSentAlerts));
                          }
                        }}
                        className="whatsapp-btn"
                        style={sentWhatsAppAlerts.includes(item.receipt_no) ? { backgroundColor: "#28a745", borderColor: "#28a745", color: "#fff" } : {}}
                      >
                        {sentWhatsAppAlerts.includes(item.receipt_no) ? "WhatsApp அனுப்பப்பட்டது" : "WhatsApp அனுப்பு"}
                      </button>
                      <a
                        href={`sms:${item.mobile_number}?body=${encodeURIComponent(getTamilAlertMessage(item.name, item.receipt_no, item.pawnjewelry_date))}`}
                        onClick={() => {
                          if (!sentSmsAlerts.includes(item.receipt_no)) {
                            const newSentAlerts = [...sentSmsAlerts, item.receipt_no];
                            setSentSmsAlerts(newSentAlerts);
                            localStorage.setItem("sentSmsAlerts", JSON.stringify(newSentAlerts));
                          }
                        }}
                        style={{
                          backgroundColor: sentSmsAlerts.includes(item.receipt_no) ? "#28a745" : "#007BFF",
                          color: "#fff",
                          padding: "5px 5px",
                          borderRadius: "5px",
                          textDecoration: "none",
                          display: "inline-block",
                        }}
                      >
                        {sentSmsAlerts.includes(item.receipt_no) ? "SMS அனுப்பப்பட்டது" : "SMS அனுப்பு"}
                      </a>
                      <button
                        onClick={() => downloadNotice(item)}
                        className="notice-btn"
                        style={{
                          backgroundColor: "#17a2b8",
                          color: "#fff",
                          border: "none",
                          padding: "5px 5px",
                          borderRadius: "5px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        Notice Download
                      </button>
                    </div>
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: "5px",
                      fontWeight: "600",
                      fontSize: "12px",
                      backgroundColor:
                        item.status === "Closed"
                          ? "#28a745"
                          : "#dc3545",
                      color: "white",
                    }}
                  >
                    {item.status === "Closed" ? "நகை மீட்கபட்டது" : "நகை மீட்கபடவில்லை"}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="18" className="text-center">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  // If not authorized, show beautiful password verification screen
  if (!authorized) {
    return (
      <div
        className="login-bg"
        style={{
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px"
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg="5" md="8" xs="12">
              <div
                className="shadow login-box p-4 bg-white rounded border-0"
                style={{
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)"
                }}
              >
                <div className="text-center mb-4">
                  <div
                    className="mx-auto bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "60px", height: "60px", fontSize: "28px" }}
                  >
                    <AiFillGolden />
                  </div>
                  <h4 className="mt-3 fw-bold text-dark">High Value Gold Loans</h4>
                  <p className="text-muted small">
                    இந்த அறிக்கை பாதுகாப்பானது. பார்க்க உங்கள் கடவுச்சொல்லை உள்ளிடவும்.
                    <br />
                    (This report is secured. Enter your password to unlock.)
                  </p>
                </div>

                {(currentUser && currentUser.login_User_Name) && (
                  <div className="mb-3 text-center bg-light p-2 rounded">
                    <span className="text-muted small">பயனர் பெயர் (Username): </span>
                    <strong className="text-dark">{currentUser.login_User_Name}</strong>
                  </div>
                )}

                {authError && <Alert variant="danger" className="text-center py-2">{authError}</Alert>}

                <form onSubmit={handleVerify}>
                  {!(currentUser && currentUser.login_User_Name) && (
                    <div className="py-2">
                      <TextInputForm
                        placeholder="User Name"
                        value={usernameInput}
                        onChange={(e) => setUsernameInput(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="py-2">
                    <TextInputForm
                      placeholder="Password"
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      suffix_icon={showPassword ? <VscEye onClick={() => setShowPassword(false)} /> : <VscEyeClosed onClick={() => setShowPassword(true)} />}
                    />
                  </div>

                  <div className="py-3 text-center">
                    <ClickButton
                      label={verifying ? <Spinner animation="border" size="sm" /> : "Unlock Report"}
                      onClick={handleVerify}
                      disabled={verifying}
                      type="submit"
                    />
                  </div>
                </form>
              </div>
            </Col>
          </Row>
        </Container>
        <ToastContainer />
      </div>
    );
  }

  // Once unlocked, show fully loaded interface
  return (
    <Container>
      {loading ? (
        <div className="spinner-container">
          <LoadingOverlay isLoading={loading} />
        </div>
      ) : (
        <>
          <Row>
            <Col lg="3" md="6" xs="12" className="py-3">
              <div className="counter-card cyan">
                <span><MdOutlinePerson /></span>
                <span className="count-numbers plus bold">{totalRecords}</span>
                <span className="count-name regular">வாடிக்கையாளர்</span>
              </div>
            </Col>
            <Col lg="3" md="6" xs="12" className="py-3">
              <div className="counter-card blue">
                <span><AiFillGolden /></span>
                <span className="count-numbers plus bold">{totalPawnRecords}</span>
                <span className="count-name regular">High Value நகை அடகு</span>
              </div>
            </Col>
            <Col lg="3" md="6" xs="12" className="py-3">
              <div className="counter-card green">
                <span><RiDeviceRecoverLine /></span>
                <span className="count-numbers plus bold">{pawnrecovertotalRecords}</span>
                <span className="count-name regular">High Value நகை மீட்பு</span>
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <h5 className="mb-3">High Value நகை அடகு விவரங்கள் (₹20,000 and above)</h5>

              <Row className="mb-3">
                <Col lg="2" md="4" xs="6" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>From Date (முதல்)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={handleInputChange(setFromDate)}
                  />
                </Col>
                <Col lg="2" md="4" xs="6" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>To Date (வரை)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={handleInputChange(setToDate)}
                  />
                </Col>
                <Col lg="2" md="4" xs="6" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>Status</label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={handleInputChange(setStatusFilter)}
                  >
                    <option value="">All</option>
                    <option value="active">Active (நிலுவையில்)</option>
                    <option value="recovered">Recovered (மீட்கபட்டது)</option>
                  </select>
                </Col>
                <Col lg="2" md="3" xs="6" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>From Days (வட்டி நிலுவை)</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0"
                    value={fromDays}
                    onChange={handleInputChange(setFromDays)}
                  />
                </Col>
                <Col lg="2" md="3" xs="6" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>To Days</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="365"
                    value={toDays}
                    onChange={handleInputChange(setToDays)}
                  />
                </Col>
                <Col lg="2" md="3" xs="6" className="mb-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="warning-btn-custom w-100 fw-bold shadow-sm"
                    onClick={() => {
                      setFromDays("365");
                      setToDays("");
                      triggerSearch();
                    }}
                    style={{
                      fontSize: "12px",
                      backgroundColor: "#ffc107",
                      color: "#000",
                      border: "1px solid #e0a800",
                      borderRadius: "5px",
                      height: "35px"
                    }}
                  >
                    1 Year+ (365 Days)
                  </button>
                </Col>
                <Col lg="3" md="6" xs="12" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="மொபைல் எண், அடகு எண் அல்லது வாடிக்கையாளர் பெயர்"
                    value={searchTerm}
                    onChange={handleInputChange(setSearchTerm)}
                  />
                </Col>
                <Col lg="2" md="6" xs="12" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px" }}>Place</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="இடம்"
                    value={placeSearchTerm}
                    onChange={handleInputChange(setPlaceSearchTerm)}
                  />
                </Col>
                <Col lg="2" md="4" xs="12" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px", color: "red", fontWeight: "bold" }}>Alert Status</label>
                  <select
                    className="form-control"
                    value={alertStatusFilter}
                    onChange={handleInputChange(setAlertStatusFilter)}
                  >
                    <option value="">All</option>
                    <option value="sent">Sent</option>
                    <option value="unsent">Unsent</option>
                  </select>
                </Col>
                <Col lg="5" md="12" xs="12" className="mb-2">
                  <label style={{ fontSize: "12px", marginBottom: "2px", display: 'block' }}>&nbsp;</label>
                  <style>
                    {`
                      .dashboard-action-buttons {
                        display: flex !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                      }
                      .dashboard-action-buttons button {
                        display: inline-flex !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                        align-items: center;
                        justify-content: center;
                      }
                    `}
                  </style>
                  <div className="dashboard-action-buttons" style={{ width: '100%', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleClear}
                      className="warning-btn-custom w-100 fw-bold shadow-sm"
                      style={{ flexGrow: 1, backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      Clear Filters
                    </button>
                    <button
                      type="button"
                      onClick={clearSentAlerts}
                      className="warning-btn-custom w-100 fw-bold shadow-sm"
                      style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', minWidth: '50px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                      title="Clear Sent Alerts"
                    >
                      <MdRefresh size={20} /> Clear Alerts
                    </button>
                    <button
                      type="button"
                      onClick={exportToExcel}
                      className="warning-btn-custom w-100 fw-bold shadow-sm"
                      style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      Export Excel
                    </button>
                    <button
                      type="button"
                      onClick={exportToPDF}
                      className="warning-btn-custom w-100 fw-bold shadow-sm"
                      style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      Export PDF
                    </button>
                  </div>
                </Col>
              </Row>

              <div className="position-relative">
                <div className="my-3">
                  {renderTable(userData)}
                </div>

                <LoadingOverlay isLoading={tableLoading} />
              </div>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredPawnRecords)} of{" "}
                  {filteredPawnRecords} records
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-primary d-flex align-items-center"
                    style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <GrPrevious />
                  </button>
                  <span className="fw-bold fs-6">Page {currentPage}</span>
                  <button
                    className="btn btn-outline-primary d-flex align-items-center"
                    style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                    disabled={currentPage * itemsPerPage >= filteredPawnRecords}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <GrNext />
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
      <ToastContainer />
      <Modal
        show={showImageModal}
        onHide={() => {
          setShowImageModal(false);
          setScale(1);
          setPosition({ x: 0, y: 0 });
          setIsDragging(false);
        }}
        size="xl"
        centered
      >
        <Modal.Body className="p-0 position-relative text-center" style={{ backgroundColor: '#111', borderRadius: '8px', overflow: 'hidden' }}>
          <button
            onClick={() => {
              setShowImageModal(false);
              setScale(1);
              setPosition({ x: 0, y: 0 });
              setIsDragging(false);
            }}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontSize: '20px',
              fontWeight: 'bold',
              lineHeight: '32px',
              cursor: 'pointer',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              color: '#333'
            }}
            title="Close"
          >
            &times;
          </button>
          {selectedImage && (
            <div
              style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                overflow: 'hidden',
                cursor: isDragging ? 'grabbing' : (scale > 1 ? 'grab' : 'zoom-in')
              }}
              onWheel={(e) => {
                const delta = e.deltaY * -0.001;
                setScale(prevScale => {
                  const newScale = Math.min(Math.max(0.5, prevScale + delta), 4);
                  if (newScale === 1) {
                    setPosition({ x: 0, y: 0 });
                  }
                  return newScale;
                });
              }}
              onMouseDown={(e) => {
                if (scale > 1) {
                  setIsDragging(true);
                  setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
                }
              }}
              onMouseMove={(e) => {
                if (!isDragging || scale <= 1) return;
                const x = e.clientX - startPos.x;
                const y = e.clientY - startPos.y;
                setPosition({ x, y });
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
            >
              <img
                src={selectedImage}
                alt="Proof Full View"
                style={{
                  maxWidth: "100%",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: '4px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transition: isDragging ? 'none' : 'transform 0.05s ease-out',
                  transformOrigin: 'center',
                  userSelect: 'none',
                  WebkitUserDrag: 'none'
                }}
              />
            </div>
          )}
          <div style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: '#aaa', padding: '12px', fontSize: '13px', borderTop: '1px solid #222' }}>
            Use mouse wheel to zoom. Click and drag to move the image when zoomed in.
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default HighValueGoldLoans;
